import FigmaIcon from "@/assets/figma-icon.svg";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useRelativeTime } from "@/hooks/use-relative-time";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { ExternalLink, FileText, RefreshCw } from "lucide-react";
import { useState } from "react";
import type { FigmaClientErrorCode, FigmaContextError, FigmaImportRow } from "../api/figma-api";
import { type FigmaCardState, deriveFigmaCardState } from "../figma-card-state";
import type { FigmaContextScopeValue } from "../figma-context-scope";
import { figmaErrorMessage, isFigmaSetupCode } from "../figma-messages";
import type { FigmaLinkTarget } from "../figma-url";
import {
  useFigmaImportQuery,
  useFigmaViewRefresh,
  useImportFigmaContextMutation,
  useRefreshFigmaContextMutation,
} from "../hooks/use-figma-import";
import { FigmaImportDialog } from "./figma-import-dialog";

interface FigmaContextCardProps {
  target: FigmaLinkTarget;
  scope: FigmaContextScopeValue;
}

function errorCodeOf(error: FigmaContextError | null): FigmaClientErrorCode | null {
  return error?.code ?? null;
}

/**
 * CR-126 stage 3: one pasted Figma link's import state, under the message
 * that holds it (D7). The original link is always shown.
 */
export function FigmaContextCard({ target, scope }: FigmaContextCardProps) {
  const { projectId } = scope;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lastIncludeVisuals, setLastIncludeVisuals] = useState(false);
  const rowQuery = useFigmaImportQuery(projectId, target.fileKey, target.nodeId);
  const importMutation = useImportFigmaContextMutation(projectId);
  const refreshMutation = useRefreshFigmaContextMutation(projectId);

  const row = rowQuery.data;
  useFigmaViewRefresh(row, refreshMutation.mutate);

  const state = deriveFigmaCardState({
    row,
    rowLoading: rowQuery.isLoading,
    rowFetching: rowQuery.isFetching,
    rowFailed: rowQuery.isError,
    importPending: importMutation.isPending,
    importSucceeded: importMutation.isSuccess,
    importErrorCode: errorCodeOf(importMutation.error),
    refreshPending: refreshMutation.isPending,
  });

  const runImport = (includeVisuals: boolean) => {
    setLastIncludeVisuals(includeVisuals);
    importMutation.mutate({ sourceUrl: target.sourceUrl, includeVisuals });
  };

  const retry = () => {
    if (rowQuery.isError) {
      void rowQuery.refetch();
      return;
    }
    runImport(lastIncludeVisuals);
  };

  return (
    <div
      data-testid="figma-context-card"
      data-state={state.kind}
      className="bg-background text-foreground mt-2 flex max-w-md flex-col gap-2 rounded-lg border p-3 text-sm"
    >
      <div className="flex items-center gap-2">
        <FigmaIcon className="size-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate font-medium">
          {state.kind === "ready" || state.kind === "refreshing" || state.kind === "stale"
            ? (state.row.target_name ?? "Figma design")
            : "Figma design"}
        </span>
        <FigmaStatusBadge state={state} />
      </div>
      <FigmaCardBody
        state={state}
        scope={scope}
        refreshErrorCode={
          refreshMutation.variables?.reason === "view" ? null : errorCodeOf(refreshMutation.error)
        }
        onImportClick={() => setDialogOpen(true)}
        onRetry={retry}
        onRefresh={(reason) =>
          state.kind === "ready" || state.kind === "stale" || state.kind === "refreshing"
            ? refreshMutation.mutate({ importId: state.row.id, reason })
            : undefined
        }
      />
      <a
        href={target.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="figma-context-card-link"
        className="text-muted-foreground hover:text-foreground flex items-center gap-1 truncate text-xs underline-offset-2 hover:underline"
      >
        <ExternalLink className="size-3 shrink-0" />
        <span className="truncate">{target.sourceUrl}</span>
      </a>
      <FigmaImportDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        targetLabel={target.sourceUrl}
        onImport={({ includeVisuals }) => runImport(includeVisuals)}
      />
    </div>
  );
}

function FigmaStatusBadge({ state }: { state: FigmaCardState }) {
  if (state.kind === "stale") {
    return (
      <Badge variant="outline" className="border-amber-500/50 text-amber-700 dark:text-amber-400">
        Stale
      </Badge>
    );
  }
  if (state.kind === "ready" || state.kind === "refreshing") {
    return (
      <Badge
        variant="secondary"
        data-testid="figma-context-badge"
        data-visuals={state.row.include_visuals ? "true" : "false"}
      >
        {state.row.include_visuals ? "Imported with visuals" : "Imported"}
      </Badge>
    );
  }
  return null;
}

interface FigmaCardBodyProps {
  state: FigmaCardState;
  scope: FigmaContextScopeValue;
  refreshErrorCode: ReturnType<typeof errorCodeOf>;
  onImportClick: () => void;
  onRetry: () => void;
  onRefresh: (reason: "manual" | "retry") => void;
}

function FigmaCardBody({
  state,
  scope,
  refreshErrorCode,
  onImportClick,
  onRetry,
  onRefresh,
}: FigmaCardBodyProps) {
  switch (state.kind) {
    case "loading":
      return <Spinner className="text-muted-foreground size-4" />;
    case "not_imported":
      return (
        <div className="flex flex-col gap-2">
          <p className="text-muted-foreground">
            Import this design so the project and Kairo's AI can use its content.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="self-start"
            data-testid="figma-context-import"
            onClick={onImportClick}
          >
            Import design context
          </Button>
        </div>
      );
    case "importing":
      return (
        <p className="text-muted-foreground flex items-center gap-2">
          <Spinner className="size-4" />
          Importing design context…
        </p>
      );
    case "setup_required":
      return (
        <div className="flex flex-col gap-2">
          <p data-testid="figma-context-setup" className="text-muted-foreground">
            {figmaErrorMessage(state.code)}
          </p>
          <Button size="sm" variant="outline" className="self-start" onClick={onRetry}>
            Retry
          </Button>
        </div>
      );
    case "error":
      return (
        <div className="flex flex-col gap-2">
          <p data-testid="figma-context-error" className="text-destructive">
            {figmaErrorMessage(state.code)}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="self-start"
            data-testid="figma-context-retry"
            onClick={onRetry}
          >
            Retry
          </Button>
        </div>
      );
    default:
      return (
        <FigmaImportedBody
          row={state.row}
          refreshing={state.kind === "refreshing"}
          stale={state.kind === "stale"}
          scope={scope}
          refreshErrorCode={refreshErrorCode}
          onRefresh={onRefresh}
        />
      );
  }
}

interface FigmaImportedBodyProps {
  row: FigmaImportRow;
  refreshing: boolean;
  stale: boolean;
  scope: FigmaContextScopeValue;
  refreshErrorCode: ReturnType<typeof errorCodeOf>;
  onRefresh: (reason: "manual" | "retry") => void;
}

function FigmaImportedBody({
  row,
  refreshing,
  stale,
  scope,
  refreshErrorCode,
  onRefresh,
}: FigmaImportedBodyProps) {
  const refreshedAgo = useRelativeTime(row.last_successful_refresh_at);
  const staleCode = row.last_refresh_error_code;
  return (
    <div className="flex flex-col gap-2">
      {row.summary ? (
        <p data-testid="figma-context-summary" className="text-muted-foreground line-clamp-3">
          {row.summary}
        </p>
      ) : null}
      {stale ? (
        <p data-testid="figma-context-stale" className="text-amber-700 dark:text-amber-400">
          {staleCode && isFigmaSetupCode(staleCode)
            ? figmaErrorMessage(staleCode)
            : "The last refresh failed. The previous import is still shown."}
        </p>
      ) : null}
      {refreshErrorCode ? (
        <p data-testid="figma-context-refresh-error" className="text-destructive text-xs">
          {figmaErrorMessage(refreshErrorCode)}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <Link
          to="/workspaces/$slug/projects/$projectSlug/docs/$docId"
          params={{
            slug: scope.workspaceSlug,
            projectSlug: scope.projectSlug,
            docId: row.document_container_id,
          }}
          data-testid="figma-context-document"
          className="text-primary flex items-center gap-1 font-medium underline-offset-2 hover:underline"
        >
          <FileText className="size-3.5" />
          Open the design document
        </Link>
        {refreshedAgo ? (
          <span className="text-muted-foreground">Refreshed {refreshedAgo}</span>
        ) : null}
        <Button
          size="xs"
          variant="ghost"
          disabled={refreshing}
          data-testid={stale ? "figma-context-retry" : "figma-context-refresh"}
          onClick={() => onRefresh(stale ? "retry" : "manual")}
          className={cn("gap-1", refreshing && "opacity-70")}
        >
          {refreshing ? <Spinner className="size-3" /> : <RefreshCw className="size-3" />}
          {stale ? "Retry" : "Refresh"}
        </Button>
      </div>
    </div>
  );
}
